import React from 'react';
import styled from 'styled-components';
import { semiBoldFontWeight } from '../../cssVariables';
import {
  failIfValidOrNonExhaustive,
} from '../../Utils';
// import { gridSmallMediaHeight } from '../Grid';
import {
  primaryColor1,
  secondaryColor2,
} from '../Utils';
import { NumberSign } from '../Utils';
import Loading from './Loading';
import {
  ArrowIconContainer as ArrowIconContainerBase,
} from './Utils';

//#region Styling
const smallBrowserWidth = 1170;
const extraSmallBrowserWidth = 800;
const height = 30; // in px
const Root = styled.li`
  display: flex;
  align-items: center;
  margin: 0 1rem;

  @media screen and (max-width: ${extraSmallBrowserWidth}px) {
    margin: 0 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;
const ribbonTriangleWidth = 8; // in px
const ribbonBackgroundColor = secondaryColor2;
const textColor = '#666';
const ribbonHorizontalPadding = '0.625rem';

const Ribbon = styled.div`
  background-color: ${ribbonBackgroundColor};
  color: ${textColor};
  padding-right: ${ribbonHorizontalPadding};
  padding-left: ${ribbonHorizontalPadding};
  min-height: ${height}px;
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  position: relative;
  margin-right: ${ribbonTriangleWidth}px;
  line-height: 1.5;

  &::after {
    content: '';
    width: 0;
    height: 100%;
    border-top: ${height / 2}px solid ${ribbonBackgroundColor};
    border-bottom: ${height / 2}px solid ${ribbonBackgroundColor};
    border-right: ${ribbonTriangleWidth}px solid transparent;
    position: absolute;
    top: 0;
    bottom: 0;
    right: ${-ribbonTriangleWidth}px;
  }

  @media screen and (max-width: ${extraSmallBrowserWidth}px) {
    padding: 0;
    margin: 0 0 0.5rem;
    min-height: 0;
    font-size: 0.7rem;

    &::after {
      border-top: ${height / 4}px solid ${ribbonBackgroundColor};
      border-bottom: ${height / 4}px solid ${ribbonBackgroundColor};
      border-right: ${ribbonTriangleWidth / 2}px solid transparent;
      right: ${-ribbonTriangleWidth / 2}px;
    }
  }
`;
const Text = styled.div`
  color: ${primaryColor1};
  text-transform: uppercase;
`;
const LargeText = styled(Text)`
  font-size: 1rem;
  font-weight: ${semiBoldFontWeight};

  @media (min-width: ${smallBrowserWidth}px) {
    font-size: 1.25rem;
  }

  @media screen and (max-width: ${extraSmallBrowserWidth}px) {
    font-size: 0.7rem;
  }
`;
const LightText = styled(Text)`
  font-size: 0.75rem;
  text-transform: none;
  font-weight: ${semiBoldFontWeight};

  @media (min-width: ${smallBrowserWidth}px) {
    font-size: 0.875rem;
  }

  @media screen and (max-width: ${extraSmallBrowserWidth}px) {
    font-size: 0.7rem;
  }
`;
const SmallText = styled(Text)`
  font-size: 0.75rem;
  font-weight: 400;
  margin-left: 0.3rem;

  @media (min-width: ${smallBrowserWidth}px) {
    font-size: 0.875rem;
  }

  @media screen and (max-width: ${extraSmallBrowserWidth}px) {
    font-size: 0.7rem;
  }
`;
const OrdinalSuffix = styled(Text.withComponent('sup'))`
  font-size: 75%;
  position: relative;
  top: -0.5em;

  @media screen and (max-width: ${extraSmallBrowserWidth}px) {
    font-size: 0.45rem;
  }
`;
const ArrowIconContainerDown = styled(ArrowIconContainerBase)`
  margin: 0 5px;
`;
const ArrowIconContainerUp = styled(ArrowIconContainerDown)`
  transform: rotate(180deg);
`;

const FlexRoot = styled.div`
  display: flex;
  align-items: center;
`;
//#endregion

const spinner = (
  <Loading width={32} height={8} dotSize={8} extraStyles={{marginLeft: '5px'}}/>
);

export enum Type {
  // texts e.g. "$4.23B", "C+"
  Text,
  // ranks e.g."45th of 128"
  Rank,
  // e.g. "10 positions over 10 years"
  TemporalChange,

}

export enum TextWeight {
  Heavy,
  Light,
  Bold,
}

export enum Status {
  Loading,
  Success,
  Failure,
}

type Data =
{
  type: Type.Text
  status: Status.Loading,
} | {
  type: Type.Text
  status: Status.Failure,
} | {
  type: Type.Text
  status: Status.Success
  text: string,
  weight: TextWeight,
} | {
  type: Type.Rank,
  status: Status.Loading,
} | {
  type: Type.Rank,
  status: Status.Failure,
} | {
  type: Type.Rank,
  status: Status.Success
  numerator: number | null
  suffix: string
  denominator: string | React.ReactElement<any>,
} | {
  type: Type.TemporalChange,
  status: Status.Loading
  unit: string,
} | {
  type: Type.TemporalChange,
  status: Status.Failure,
} | {
  type: Type.TemporalChange,
  status: Status.Success
  quantity: number
  changeDirection: NumberSign
  unit: string,
};

type IProps = Data & {
  label: string | React.ReactElement<any>,
};
export default (props: IProps) => {
  const {label} = props;
  let content: React.ReactElement<any>;
  if (props.type === Type.Text) {
    if (props.status === Status.Success) {
      const TextContainer = (props.weight === TextWeight.Heavy) ? LargeText : LightText;
      content = (
        <TextContainer>{props.text}</TextContainer>
      );
    } else if (props.status === Status.Loading) {
      content = spinner;
    } else if (props.status === Status.Failure) {
      content = (<div/>);
    } else {
      failIfValidOrNonExhaustive(props, 'Invalid status ' + props);
      content = (<div/>);
    }
  } else if (props.type === Type.Rank) {
    if (props.status === Status.Success) {
      if (props.numerator === null) {
        content = (
          <LargeText>N/A</LargeText>
        );
      } else {
        content = (
          <FlexRoot>
            <LargeText>
              {props.numerator}
            </LargeText>
            <OrdinalSuffix>{props.suffix}</OrdinalSuffix>
            <SmallText>{props.denominator}</SmallText>
          </FlexRoot>
        );
      }
    } else if (props.status === Status.Loading) {
      content = spinner;
    } else if (props.status === Status.Failure) {
      content = (<div/>);
    } else {
      failIfValidOrNonExhaustive(props, 'Invalid status ' + props);
      content = (<div/>);
    }
  } else if (props.type === Type.TemporalChange) {
    let arrowIcon: React.ReactElement<any> | null;
    if (props.status === Status.Success) {
      if (props.changeDirection !== NumberSign.Zero) {
        let ArrowIconContainer: typeof ArrowIconContainerDown | null;
        if (props.changeDirection === NumberSign.Positive) {
          ArrowIconContainer = ArrowIconContainerUp;
        } else if (props.changeDirection === NumberSign.Negative) {
          ArrowIconContainer = ArrowIconContainerDown;
        } else {
          failIfValidOrNonExhaustive(
            props.changeDirection,
            'Invalid temporal change direction ' + props.changeDirection,
          );
          // These lines will never be executed:
          ArrowIconContainer = ArrowIconContainerDown;
        }
        const arrowHeight = window.innerWidth > extraSmallBrowserWidth ? height * 0.7 : height * 0.4;
        arrowIcon = (
          <ArrowIconContainer
            color={primaryColor1}
            height={arrowHeight}
            dangerouslySetInnerHTML={{__html: require('../sidebar/arrow.svg')}}
          />
        );
      } else {
        arrowIcon = null;
      }
      content = (
        <FlexRoot>
          {arrowIcon}
          <LargeText>{props.quantity}</LargeText>
          <SmallText>{props.unit}</SmallText>
        </FlexRoot>
      );
    } else if (props.status === Status.Loading) {
      content = (
        <>
          <LargeText>{spinner}</LargeText>
          <SmallText>{props.unit}</SmallText>
        </>
      );
    } else if (props.status === Status.Failure) {
      content = (<div/>);
    } else {
      failIfValidOrNonExhaustive(props, 'Invalid status ' + props);
      content = (<div/>);
    }
  } else {
    failIfValidOrNonExhaustive(props, 'Invalid indicator type ' + props);
    content = (<div/>);
  }
  return (
    <Root>
      <Ribbon>
        {label}
      </Ribbon>
      {content}
    </Root>
  );
};
